import type { Color } from '../../../types';

/**
 * Vibrant color presets for artistic mode
 * Enhanced for high contrast and visual appeal
 */
export const COLOR_PRESETS: Color[] = [
  { r: 255, g: 50, b: 150, a: 1 },   // Vibrant Hot Pink
  { r: 0, g: 240, b: 255, a: 1 },    // Electric Cyan
  { r: 100, g: 255, b: 50, a: 1 },   // Neon Lime
  { r: 255, g: 200, b: 0, a: 1 },    // Bright Gold
  { r: 150, g: 50, b: 255, a: 1 },   // Electric Purple
  { r: 255, g: 80, b: 0, a: 1 },     // Bright Orange
  { r: 0, g: 255, b: 150, a: 1 },    // Mint Green
  { r: 255, g: 0, b: 200, a: 1 },    // Magenta
];

/**
 * Current color index for cycling
 */
let currentColorIndex = 0;

/**
 * Get the current color preset
 */
export function getCurrentColor(): Color {
  const color = COLOR_PRESETS[currentColorIndex];
  if (!color) {
    return COLOR_PRESETS[0]!;
  }
  return color;
}

/**
 * Cycle to the next color preset
 * @returns The new current color
 */
export function cycleColor(): Color {
  currentColorIndex = (currentColorIndex + 1) % COLOR_PRESETS.length;
  const color = COLOR_PRESETS[currentColorIndex];
  if (!color) {
    return COLOR_PRESETS[0]!;
  }
  return color;
}

/**
 * Reset color cycling to the first preset
 */
export function resetColorCycle(): void {
  currentColorIndex = 0;
}

/**
 * Interpolate between two colors
 * @param color1 - Starting color
 * @param color2 - Ending color
 * @param t - Interpolation factor (0-1)
 * @returns Interpolated color
 */
export function interpolateColor(color1: Color, color2: Color, t: number): Color {
  const clampedT = Math.max(0, Math.min(1, t));
  
  return {
    r: Math.round(color1.r + (color2.r - color1.r) * clampedT),
    g: Math.round(color1.g + (color2.g - color1.g) * clampedT),
    b: Math.round(color1.b + (color2.b - color1.b) * clampedT),
    a: color1.a + (color2.a - color1.a) * clampedT,
  };
}

/**
 * Convert Color to CSS rgba string
 * @param color - Color to convert
 * @returns CSS rgba string
 */
export function colorToRgba(color: Color): string {
  return `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`;
}
